<template>
	<div class="select-wrapper" :class="{expanded: forceOpen}" :tabindex="forceOpen ? null : 0" @focusout="focusOut">
		<div v-if="label"><p class="txt-input-label">{{ label }}</p></div>
		<div class="select-element" :class="{'no-radius':open, relative}">
			<div v-if="!forceOpen" @click="changeDropdownState(!open)" class="select-title row centered space-between">
				<div class="select-title-text"><p>{{ getTitle }}</p></div>
				<div>
					<AppChevronIcon :direction="chevronDirection"/>
				</div>
			</div>
			<AppDropdown
				:absolute="!relative && !forceOpen"
				:collapsed="!isOpen">
				<div ref="dropdown" class="scroll-container">
					<div
						v-for="(item, index) in values"
						:key="index"
						class="select-option"
					>
						<AppCheckbox
							:isActive="checkedIndexes.includes(index)"
							:label="item"
							:auto-toggle="false"
							@check-toggle="(checked) => elementToggled(checked, index)"
						></AppCheckbox>
					</div>
				</div>
			</AppDropdown>
		</div>
	</div>
</template>

<script>
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppCheckbox from "@/shared/components/AppCheckbox.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";

import {useLanguageStore} from "@/shared/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString}
	},
	name: "AppChecklist",
	emits: ["change-checked", "toggling-dropdown", "toggled-dropdown"],
	components: {AppChevronIcon, AppDropdown, AppCheckbox},
	data() {
		return {
			open: false,
			moving: false,
			checkedIndexes: [],
		}
	},
	props: {
		title: {type: String, required: true},
		values: {type: Array, required: true},
		label: {type: String, default: null},
		selectedIndexes: {type: Array, default: []},
		maxSelectable: {type: Number, default: null},
		relative: {type: Boolean, default: false},
		forceOpen: {type: Boolean, default: false},
	},
	methods: {
		focusOut() {
			if(this.forceOpen) { return; }
			this.changeDropdownState(false);
		},
		changeDropdownState(newState) {
			if (!this.moving) {
				this.moving = true;
				this.open = newState;
				this.$emit("toggling-dropdown", newState);
				setTimeout(() => {
					this.moving = false;
					this.$emit("toggled-dropdown", newState);
				}, 300);
			}
		},
		elementToggled(checked, index) {
			let triggeredValue = this.values[index];
			if (checked) {
				if (this.maxSelectable !== null && this.checkedIndexes.length >= this.maxSelectable) {
					return;
				}
				this.checkedIndexes.push(index);
			} else {
				this.checkedIndexes.splice(this.checkedIndexes.indexOf(index), 1);
			}

			this.$emit("change-checked", this.checkedIndexes, this.values.filter((item, i) => this.checkedIndexes.includes(i)), index, triggeredValue, checked);
		},
	},
	computed: {
		isOpen() {
			return this.forceOpen || this.open;
		},
		chevronDirection() {
			return this.open ? "up" : "down";
		},
		getTitle() {
			let numSelected = this.checkedIndexes.length;
			if (numSelected === 1) {
				return this.getString("1_selected");
			} else if (numSelected > 1) {
				return this.getString("n_selected", [numSelected]);
			} else {
				return this.title;
			}
		}
	},
	created() {
		this.checkedIndexes = filterSelectedIndexes(this.selectedIndexes, this.values, this.maxSelectable);
	},
	watch: {
		selectedIndexes(newValue) {
			this.checkedIndexes = filterSelectedIndexes(newValue, this.values, this.maxSelectable);
		},
		maxSelectable(newValue) {
			this.checkedIndexes = filterSelectedIndexes(this.selectedIndexes, this.values, newValue);
		},
	}
}

function filterSelectedIndexes(selectedIndexes, values, maxSelectable) {
	let result = [];
	if (selectedIndexes && selectedIndexes.length) {
		selectedIndexes.forEach(index => {
			if (index < values.length) {
				result.push(index);
			}
		});
		if (maxSelectable !== null && result.length > maxSelectable) {
			result = result.slice(0, maxSelectable);
		}
	}
	return result;
}

</script>

<style lang="scss" scoped>

.select-wrapper {
	width: 100%;
	max-width: 250px;
	min-width: 150px;

	.select-element {
		position: relative;
		box-shadow: 0 1px 2px var(--color-box-shadow-3);
		border-radius: 13px;
		transition: border-radius .1s $transition .25s;
		background-color: var(--color-bg);

		&.no-radius:not(.relative) {
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
			transition: none;
		}
	}

	.select-title {
		@include noSelect;
		width: 100%;
		height: 40px;
		padding: 0 10px;
		cursor: pointer;

		.select-title-text {
			max-width: calc(100% - 16px);

			p {
				@include truncateString();
				font-size: 14px;
			}
		}
	}

	.dropdown-container {

		:deep(.dropdown) {
			overflow: hidden;
			padding: 0;
			border-radius: 0 0 13px 13px;

			.dropdown-content {
				margin-top: 0;
				padding: 0;
				border-radius: 0 0 13px 13px;
			}
		}

		.scroll-container {
			max-height: 210px;
			overflow-y: auto;
			background-color: var(--color-bg-primary-2);
			border-radius: 0 0 13px 13px;

			&::-webkit-scrollbar {
				width: 4px;
				height: 4px;
				background-color: transparent;
			}

			&::-webkit-scrollbar-thumb {
				background-color: var(--corlor-alpha-4);
				border-radius: 10px;
				width: 4px;
				height: 4px;
			}

			scrollbar-width: thin;
			scrollbar-color: var(--corlor-alpha-4);
		}

		&:deep(.select-option) {
			@include centered;
			@include noSelect;
			height: 35px;
			width: 100%;
			cursor: pointer;

			& * {
				@include noSelect;
			}

			& label * {
				pointer-events: none;
			}

			&.active {
				background-color: var(--color-alpha-2);

				p {
					color: var(--color-bg);
				}
			}

			&:not(:last-of-type) {
				border-bottom: 1px solid var(--color-border-alpha);
			}

			&:hover {
				background-color: var(--color-alpha-1);

				p {
					color: var(--color-txt);
				}
			}

			label {
				@include truncateString();
				width: 100%;
				text-align: left;
				font-size: 12px;
				line-height: 15px;
				padding-left: 40px;
			}

		}

	}

	&.expanded {

		.dropdown-container {

			:deep(.dropdown) {
				border-radius: 13px;

				.dropdown-content {
					border-radius: 13px;
				}
			}

			.scroll-container {
				border-radius: 13px;
			}
		}
	}
}

</style>